import { FC, useMemo } from 'react'
import { PropertyTab, propertyTabs, useClientProperty } from '../contexts'

import { Country } from 'constants/country'
import { GRAY_700 } from 'constants/styling/theme'
import { GalleryHeader } from 'components/common/Gallery/GalleryHeader'
import { GalleryNotFound } from 'components/pages/Gallery/_main/ClientGallery/common/GalleryNotFound'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Outlet } from 'react-router-dom'
import { PageContent } from 'components/common/Page/PageContent'
import { PageHeader } from 'components/common/Page/PageHeader'
import { PageLayout } from 'components/common/Page/PageLayout'
import { PageTab } from 'components/common/Page/PageTab'
import { PageTabs } from 'components/common/Page/PageTabs'
import { PropertyPageActions } from '../../../_main/PropertyPageActions'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const ClientPropertyPageLayout: FC = () => {
  const { t } = useTranslation(['property', 'common'])
  const {
    propertyData,
    handleTabChange,
    activeTab,
    setActiveTab,
    propertyFullAddress,
    showPropertyNotFound,
  } = useClientProperty()

  const activePropertyTabs = useMemo(() => {
    // Remove the document tab for non-German country
    if (propertyData?.address?.country !== Country.DE) {
      return new Set([...propertyTabs].filter((tab) => tab !== PropertyTab.DOCUMENTS))
    }

    return propertyTabs
  }, [propertyData])

  const { hasUserActiveSubscription } = useUserData()

  return (
    <PageLayout
      header={
        <PageHeader actions={<PropertyPageActions propertyId={propertyData?.id} />}>
          <GalleryHeader
            title={propertyData?.title || '-'}
            subTitle={propertyFullAddress}
          />
        </PageHeader>
      }
      tabs={
        <PageTabs>
          {[...activePropertyTabs].map(propertyTab => {
            const isTabLocked = propertyTab === PropertyTab.AI_TEXT_GENERATOR && !hasUserActiveSubscription
            const tooltipText = isTabLocked ? t('common:Subscribers_only') : undefined

            return (
              <PageTab
                key={propertyTab}
                tab={propertyTab}
                tabText={t(`tabs.${propertyTab}`)}
                activeTab={activeTab}
                disabled={isTabLocked}
                tooltipText={tooltipText}
                trailingIcon={isTabLocked &&
                  <LockOutlinedIcon fontSize="medium" sx={{ color: GRAY_700 }} />
                }
                onClick={() => {
                  setActiveTab(propertyTab)
                  handleTabChange(propertyTab)
                }}
              />
            )
          })}
        </PageTabs>
      }
      layoutErrorContent={showPropertyNotFound && <GalleryNotFound />}
    >
      <Stack width="100%" alignItems="center" justifyContent="center">
        <PageContent>
          <Outlet />
        </PageContent>
      </Stack>
    </PageLayout>
  )
}
